import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import Alert from '../components/UI/Alert';
import { Mail } from 'lucide-react';
import { sendMagicLink, supabase } from '../lib/supabase';
import { consumeAuthError } from '../lib/authErrorParams';
import { brandConfig } from '../brand';
import { useAlert } from '../hooks/useAlert';
import { useTurnstile } from '../hooks/useTurnstile';
import { validateEmail } from '../lib/formValidation';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const { alert, setAlert } = useAlert();
  const { captchaToken, turnstileRef, siteKey, isTurnstileEnabled, onSuccess, onError, onExpire, resetTurnstile } = useTurnstile();
  const navigate = useNavigate();

  // If we landed here because a magic link was expired/already-used (Supabase
  // redirects to /account with an error, which bounces to /login), tell the
  // user why and prompt them to request a fresh link — instead of a silent bounce.
  useEffect(() => {
    const authError = consumeAuthError();
    if (authError) {
      setAlert({ type: authError.type, message: authError.message });
    }
  }, [setAlert]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setAlert({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setIsSubmitting(true);
    setAlert(null);

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const { data: exists, error } = await supabase
        .rpc('check_member_email_exists', { lookup_email: normalizedEmail });

      if (error) throw error;

      if (exists) {
        // createUser: true so existing members without auth records get one created
        const { error: otpError } = await sendMagicLink(normalizedEmail, captchaToken, true);
        if (otpError) throw otpError;

        setLinkSent(true);
        resetTurnstile();
        setAlert({
          type: 'success',
          message: 'Login link sent! Check your email for a secure link to sign in.'
        });
      } else {
        navigate('/register', { state: { email: normalizedEmail } });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message
        : (typeof error === 'object' && error !== null && 'message' in error)
          ? String((error as { message: unknown }).message)
          : 'Unknown error';
      console.error('Login error:', message);
      resetTurnstile();

      // Surface actionable errors; fall back to generic for anything unexpected
      if (message.includes('captcha') || message.includes('Captcha')) {
        setAlert({ type: 'error', message: 'Security check failed. Please refresh the page and try again.' });
      } else if (message.includes('rate limit') || message.includes('429')) {
        setAlert({ type: 'error', message: 'Too many attempts. Please wait a few minutes and try again.' });
      } else if (message.includes('Email not confirmed')) {
        setAlert({ type: 'error', message: 'Your email address has not been confirmed. Check your inbox for a confirmation link.' });
      } else {
        setAlert({ type: 'error', message: `Sign in failed: ${message}. Please try again or contact support.` });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-primary-100">
            <Mail className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="mt-3 text-3xl font-bold text-gray-900">Sign In</h1>
          <p className="text-lg text-gray-600 mt-2">
            Access your {brandConfig.name} membership account
          </p>
        </div>

        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={() => setAlert(null)}
            className="mb-6"
          />
        )}

        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          <p className="text-sm text-gray-600 mb-6">
            Enter your email address and we'll send you a secure login link.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="you@example.com"
                required
              />
            </div>

            {linkSent ? (
              <>
                <Button
                  type="button"
                  onClick={() => {
                    setAlert(null);
                    setEmail('');
                    setLinkSent(false);
                    resetTurnstile();
                  }}
                  variant="outline"
                  fullWidth
                >
                  Try a Different Email
                </Button>
                <p className="text-center text-sm text-gray-500 mt-3">
                  Can't find it?{' '}
                  <Link to="/email-help" className="text-primary-600 hover:text-primary-500">
                    Help me find my login email
                  </Link>
                </p>
              </>
            ) : (
              <>
                {isTurnstileEnabled && (
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={siteKey!}
                      onSuccess={onSuccess}
                      onError={onError}
                      onExpire={onExpire}
                    />
                  </div>
                )}
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isTurnstileEnabled && !captchaToken}
                  fullWidth
                >
                  {isSubmitting ? 'Checking...' : 'Continue'}
                </Button>
              </>
            )}
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Not a member yet?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-primary-600 hover:text-primary-500"
              >
                Join Now
              </button>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
